import {
  Button,
  Col,
  Form,
  message,
  Row,
  Tabs,
} from "antd";
import React, { useContext, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { PhysioInitialContext } from "./PhysiotherapistInitialAssessmentProvider";
import {
  physioInspectionAPI,
  updatePhysioInspectionAPI,
} from "../../../../services/physio.services";
import DoctorHistoryForm from "../../InspectionForms/HistoryForm/DoctorHistoryForm";
import PhysioFunctionalAssessment from "../../InspectionForms/FunctionalStatusAssessment/PhysioFunctionalAssessment";
import PhysioGeneralExaminationForm from "../../InspectionForms/Examination/GeneralExaminationForm/PhysioGeneralExaminationForm";
import PhysioSystemicExaminationForm from "../../InspectionForms/Examination/SystemicExaminationForm/PhysioSystemicExaminationForm";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import PhysioFunctionalDiagnosis from "../../InspectionForms/FunctionalDiagnosis/PhysioFunctionalDiagnosis";
import PhysioTherapeuticGoals from "../../InspectionForms/TherapeuticGoals/PhysioTherapeuticGoals";
import PhysioTherapy from "../../InspectionForms/Therapy/PhysioTherapy";
import PhysioTreatmentPlan from "../../InspectionForms/TreatmentPlan/PhysioTreatmentPlan";
import "../../ipforms.css";
import "../../Doctor/InitialInspection/InitialInspectionHeader.css";
import { appConfirmModal } from "../../../../helpers/appConfirmModal";

const physioInitialTabs = [
  { key: "1", label: "History" },
  { key: "2", label: "General Examination" },
  { key: "3", label: "Systemic Examination" },
  { key: "4", label: "Functional Status Assessment" },
  { key: "5", label: "Functional Diagnosis" },
  { key: "6", label: "Therapeutic Goals" },
  { key: "7", label: "Therapy" },
  { key: "8", label: "Treatment Plan" },
];

const PhysiotherapistInitialInspectionForm = ({
  setVisible,
  refreshPage,
  initialRefresh,
  editId,
  patientData,
  setConfirmModal,
  activeTab,
  setActiveTab,
}) => {
  const formBodyRef = useRef(null);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const [historyForm] = Form.useForm();
  const [generalForm] = Form.useForm();
  const [systemicForm] = Form.useForm();
  const [functionalForm] = Form.useForm();
  const [diagnosisForm] = Form.useForm();
  const [therapeuticForm] = Form.useForm();
  const [therapyForm] = Form.useForm();
  const [treatmentForm] = Form.useForm();
  const { formData, setFormData } = useContext(PhysioInitialContext);
  const [isDirty, setIsDirty] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);

  const handleSubmit = async (values, flag) => {
    window.scrollTo(0, 0);
    try {
      if (editId) {
        await updatePhysioInspectionAPI(editId, values);
        initialRefresh();
      } else {
        await physioInspectionAPI(values);
        refreshPage();
        initialRefresh();
      }

      message.success(`${flag} submitted successfully`);
      if (buttonPressed) {
        if (activeTab === "8") {
          setVisible(false);
        } else {
          const nextTab = (parseInt(activeTab) + 1).toString();
          setActiveTab(nextTab);
        }
        setButtonPressed(false);
      }
      setIsDirty(false);
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const onHistorySubmit = (values) => {
    handleSubmit(
      {
        history: values,
        admission_id: params?.patient,
      },
      "History"
    );
  };
  const onGeneralSubmit = (values) => {
    handleSubmit(
      {
        general_examination: { ...values },
        admission_id: params?.patient,
      },
      "General Examination"
    );
  };
  const onSystemicSubmit = (values) => {
    handleSubmit(
      {
        systemic_examination: {
          ...values,
        },
        admission_id: params?.patient,
      },
      "Systemic Examination"
    );
  };
  const onFunctionalSubmit = (values) => {
    handleSubmit(
      {
        functional_status: values,
        admission_id: params?.patient,
      },
      "Functional Status Assessment"
    );
  };
  const onDiagnosisSubmit = (values) => {
    handleSubmit(
      {
        functional_diagnosis: values,
        admission_id: params?.patient,
      },
      "Functional Diagnosis"
    );
  };
  const onTherapeuticSubmit = (values) => {
    handleSubmit(
      {
        therapeutic_goals: values,
        admission_id: params?.patient,
      },
      "Therapeutic Goals"
    );
  };
  const onTherapySubmit = (values) => {
    handleSubmit(
      {
        therapy: values,
        admission_id: params?.patient,
      },
      "Therapy"
    );
  };
  const onTreatmentSubmit = (values) => {
    handleSubmit(
      {
        treatment_plan: values,
        admission_id: params?.patient,
        intial_assessment_done: true,
      },
      "Treatment Plan"
    );
  };

  const getFormKey = () => {
    switch (activeTab) {
      case "1":
        return historyForm.submit();
      case "2":
        return generalForm.submit();
      case "3":
        return systemicForm.submit();
      case "4":
        return functionalForm.submit();
      case "5":
        return diagnosisForm.submit();
      case "6":
        return therapeuticForm.submit();
      case "7":
        return therapyForm.submit();
      case "8":
        return treatmentForm.submit();
      default:
        return "";
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      // setNextTab(key);
      appConfirmModal({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Do you want to save these changes and proceed?",
        onOk: () => {
          switch (activeTab) {
            case "1":
              historyForm.validateFields().then((values) => {
                setIsDirty(false);
                onHistorySubmit(values);
                setActiveTab(key);
              });
              break;
            case "2":
              generalForm.validateFields().then((values) => {
                setIsDirty(false);
                onGeneralSubmit(values);
                setActiveTab(key);
              });
              break;
            case "3":
              systemicForm.validateFields().then((values) => {
                setIsDirty(false);
                onSystemicSubmit(values);
                setActiveTab(key);
              });
              break;
            case "4":
              functionalForm.validateFields().then((values) => {
                setIsDirty(false);
                onFunctionalSubmit(values);
                setActiveTab(key);
              });
              break;
            case "5":
              diagnosisForm.validateFields().then((values) => {
                setIsDirty(false);
                onDiagnosisSubmit(values);
                setActiveTab(key);
              });
              break;
            case "6":
              therapeuticForm.validateFields().then((values) => {
                setIsDirty(false);
                onTherapeuticSubmit(values);
                setActiveTab(key);
              });
              break;
            case "7":
              therapyForm.validateFields().then((values) => {
                setIsDirty(false);
                onTherapySubmit(values);
                setActiveTab(key);
              });
              break;
            case "8":
              treatmentForm.validateFields().then((values) => {
                setIsDirty(false);
                onTreatmentSubmit(values);
                setActiveTab(key);
              });
              break;
            default:
              setActiveTab(key);
          }
        },
        onCancel: () => {
          setActiveTab(key);
          setIsDirty(false);
        },
      });
    } else {
      setActiveTab(key);
    }
    window.scrollTo(0, 0);
  };

  const onFormValuesChange = () => {
    setIsDirty(true);
    // refreshForm();
  };

  useEffect(() => {
    if (formBodyRef.current) {
      formBodyRef.current.scrollTop = 0;
    }
  }, [activeTab]);

useEffect(() => {
  const smwt = formData?.functional_status?.endurance?.smwt;
if (smwt?.age != null && smwt?.gender) return;
  if (!patientData?.admission?.patient) return;

  const rawAge = patientData?.admission?.patient?.age_by_dob ?? patientData?.admission?.patient?.age;
  const age = typeof rawAge === "string" ? parseInt(rawAge, 10) || null : rawAge;

  const rawGender = patientData?.admission?.patient?.gender?.toLowerCase();
  const gender =
    rawGender === "male" ? "male adult" :
    rawGender === "female" ? "female adult" :
    null;

  if (smwt?.age != null && smwt?.gender) return;

  setFormData((prev) => ({
    ...prev,
    functional_status: {
      ...prev?.functional_status,
      endurance: {
        ...prev?.functional_status?.endurance,
        smwt: {
          ...prev?.functional_status?.endurance?.smwt,
          age,
          gender,
        },
      },
    },
  }));
}, [editId, patientData, formData, setFormData]);
  return (
    <div className="initial-inspection-form-wrapper">
      <div className="ia-form-tabs">
        <Row>
          <Col span={24}>
            <Tabs
              activeKey={activeTab}
              items={physioInitialTabs}
              onChange={handleTabChange}
            />
          </Col>
        </Row>
      </div>
      <div className="ia-form-panel">
        <div className="ia-form-body" ref={formBodyRef}>
          {activeTab === "1" && (
            <Form
              id="history-form"
              form={historyForm}
              layout="vertical"
              onFinish={onHistorySubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.history}
              className="ip-forms"
            >
              <DoctorHistoryForm
                form={historyForm}
                context={PhysioInitialContext}
                editId={editId}
                patientData={patientData}
                // refreshForm={refreshForm}
              />
            </Form>
          )}
          {activeTab === "2" && (
            <Form
              id="general-form"
              form={generalForm}
              layout="vertical"
              onFinish={onGeneralSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.general_examination}
              className="ip-forms"
            >
              <PhysioGeneralExaminationForm form={generalForm} />
            </Form>
          )}
          {activeTab === "3" && (
            <Form
              id="systemic-form"
              form={systemicForm}
              layout="vertical"
              onFinish={onSystemicSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.systemic_examination}
              className="ip-forms"
            >
              <PhysioSystemicExaminationForm form={systemicForm} />
            </Form>
          )}
          {activeTab === "4" && (
            <Form
              id="functional-status-assessment"
              form={functionalForm}
              layout="vertical"
              onFinish={onFunctionalSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.functional_status}
              className="ip-forms"
            >
              <PhysioFunctionalAssessment form={functionalForm} />
            </Form>
          )}
          {activeTab === "5" && (
            <Form
              id="functional-diagnosis"
              form={diagnosisForm}
              layout="vertical"
              onFinish={onDiagnosisSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.functional_diagnosis}
              className="ip-forms"
            >
              <PhysioFunctionalDiagnosis form={diagnosisForm} />
            </Form>
          )}
          {activeTab === "6" && (
            <Form
              id="therapeutic-goals"
              form={therapeuticForm}
              layout="vertical"
              onFinish={onTherapeuticSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.therapeutic_goals}
              className="ip-forms"
            >
              <PhysioTherapeuticGoals form={therapeuticForm} />
            </Form>
          )}
          {activeTab === "7" && (
            <Form
              id="therapy"
              form={therapyForm}
              layout="vertical"
              onFinish={onTherapySubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.therapy}
              className="ip-forms"
            >
              <PhysioTherapy form={therapyForm} />
            </Form>
          )}
          {activeTab === "8" && (
            <Form
              id="treatment-plan"
              form={treatmentForm}
              layout="vertical"
              onFinish={onTreatmentSubmit}
              onValuesChange={onFormValuesChange}
              initialValues={formData?.treatment_plan}
              className="ip-forms"
            >
              <PhysioTreatmentPlan form={treatmentForm} />
            </Form>
          )}
        </div>
        <div className="ia-form-footer">
          <Button
            onClick={() => {
              getFormKey();
              setButtonPressed(true);
              document
                .querySelector(".initial-inspection-form-wrapper")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
            type="primary"
            className={btnStyles.mdBtn}
            style={{ fontWeight: 500 }}
          >
            {activeTab === "8" ? "Done" : " Save & Proceed"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhysiotherapistInitialInspectionForm;
